import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import {
  Badge,
  Button,
  Card,
  CardHead,
  ErrorBlock,
  Loading,
  PageHeader,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, Select, SecretInput, TextInput } from '../components/Field.js';
import { useToast } from '../components/Toast.js';
import { IconCheck, IconProviders } from '../components/icons.js';
import type { ProviderConfigView, ProviderField, ProviderSummary } from '../../data/port.js';

export function Providers() {
  const { data } = useApp();
  const toast = useToast();
  const admin = useCan('console-admin');
  const [testando, setTestando] = useState<string | null>(null);
  const [configurar, setConfigurar] = useState<ProviderSummary | null>(null);

  const estado = useAsync<ProviderSummary[]>(() => data.listProviders(), [data]);

  const testar = async (nome: string) => {
    setTestando(nome);
    try {
      const r = await data.testProvider(nome);
      if (r.ok) toast.success(`Conexão com "${nome}": OK`);
      else toast.error(`Conexão com "${nome}" falhou`, r.message ?? 'sem detalhe');
    } catch (e) {
      toast.error('Falha ao testar', e instanceof Error ? e.message : undefined);
    } finally {
      setTestando(null);
    }
  };

  const colunas: Column<ProviderSummary>[] = [
    { key: 'nome', header: 'Provider', cell: (p) => <span className="td--strong" style={{ textTransform: 'capitalize' }}>{p.name}</span> },
    { key: 'tipo', header: 'Tipo', cell: (p) => <Badge tone="neutral">{p.kind === 'gateway' ? 'gateway' : 'ERP'}</Badge> },
    { key: 'label', header: 'Nome', cell: (p) => <span className="td--muted">{p.label}</span> },
    {
      key: 'config',
      header: 'Config',
      cell: (p) => (p.configured ? <Badge tone="ok" dot>ok</Badge> : <Badge tone="warn" dot>falta</Badge>),
    },
    { key: 'ativo', header: 'Ativo', cell: (p) => (p.active ? <Badge tone="info">sim</Badge> : <span className="faint">—</span>) },
    ...(admin
      ? [
          {
            key: 'acoes',
            header: '',
            align: 'right' as const,
            cell: (p: ProviderSummary) => (
              <div className="row" style={{ justifyContent: 'flex-end', gap: '0.4rem' }}>
                <Button variant="ghost" size="sm" loading={testando === p.name} onClick={() => void testar(p.name)}>
                  <IconCheck size={14} /> Testar
                </Button>
                <Button variant="primary" size="sm" onClick={() => setConfigurar(p)}>
                  Configurar
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader title="Providers" desc="Gateways e ERP: chaves e URLs, cifradas no banco." />

      {!admin && (
        <div className="notice notice--info" style={{ maxWidth: 720 }}>
          Você tem acesso de leitura. Configurar chaves exige o papel de administrador.
        </div>
      )}

      <Card>
        <CardHead title="Integrações" sub="Estado da configuração de cada provider" />
        <div className="card__body--flush">
          {estado.loading && !estado.data && <Loading />}
          {estado.error && <ErrorBlock message={estado.error} />}
          {estado.data && estado.data.length === 0 && (
            <div className="empty"><IconProviders size={40} /><span>Nenhum provider registrado.</span></div>
          )}
          {estado.data && estado.data.length > 0 && (
            <DataTable columns={colunas} rows={estado.data} keyOf={(p) => p.name} />
          )}
        </div>
      </Card>

      {configurar && (
        <ConfigModal
          provider={configurar}
          onClose={() => setConfigurar(null)}
          onSalvo={() => {
            setConfigurar(null);
            estado.reload();
          }}
        />
      )}
    </>
  );
}

/** Só primitivo vira string segura no input de config. */
function isPrimitivo(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

function valorInicial(campo: ProviderField): string {
  if (campo.secret) return ''; // segredo nunca vem preenchido — branco preserva
  if (campo.options && !isPrimitivo(campo.value)) return campo.options[0] ?? '';
  return isPrimitivo(campo.value) ? String(campo.value) : '';
}

function ConfigModal({
  provider,
  onClose,
  onSalvo,
}: {
  provider: ProviderSummary;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const { data } = useApp();
  const toast = useToast();
  const vista = useAsync<ProviderConfigView>(() => data.getProvider(provider.name), [data, provider.name]);

  return (
    <Modal
      title={`Configurar · ${provider.name}`}
      sub="Segredo em branco preserva o atual — e some depois de salvo."
      onClose={onClose}
      wide
    >
      {vista.loading && !vista.data && <Loading />}
      {vista.error && <ErrorBlock message={vista.error} />}
      {vista.data && <ConfigForm nome={provider.name} fields={vista.data.fields} onClose={onClose} onSalvo={onSalvo} toast={toast} />}
    </Modal>
  );
}

function ConfigForm({
  nome,
  fields,
  onClose,
  onSalvo,
  toast,
}: {
  nome: string;
  fields: ProviderField[];
  onClose: () => void;
  onSalvo: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { data } = useApp();
  const editaveis = fields.filter((f) => !f.advanced);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(editaveis.map((f) => [f.key, valorInicial(f)])),
  );
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const set = (key: string, v: string) => setValues((atual) => ({ ...atual, [key]: v }));

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    const patch: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v && v.trim() !== '') patch[k] = v.trim();
    }
    if (Object.keys(patch).length === 0) {
      toast.info('Nada mudou', 'Nenhum campo foi alterado.');
      setSalvando(false);
      onClose();
      return;
    }
    try {
      await data.setProviderConfig(nome, patch);
      toast.success(`Config de "${nome}" salva`, 'Vale em runtime — a próxima cobrança já usa a chave nova.');
      onSalvo();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar a config.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      {erro && <div className="notice notice--danger">{erro}</div>}
      <div className="form-grid" style={{ marginBottom: '1.25rem' }}>
        {editaveis.map((campo) => {
          const req = campo.secret ? !!campo.required && !campo.set : !!campo.required;
          const hint = campo.secret && campo.set ? '•••• definido — em branco mantém' : campo.hint;
          return (
            <Field key={campo.key} label={campo.label} required={req} hint={hint}>
              {campo.options ? (
                <Select value={values[campo.key] ?? ''} onChange={(v) => set(campo.key, v)} options={campo.options} />
              ) : campo.secret ? (
                <SecretInput
                  value={values[campo.key] ?? ''}
                  onChange={(v) => set(campo.key, v)}
                  {...(campo.generate ? { generate: true } : {})}
                  placeholder={campo.set ? 'em branco mantém o atual' : 'obrigatório'}
                />
              ) : (
                <TextInput value={values[campo.key] ?? ''} onChange={(v) => set(campo.key, v)} mono />
              )}
            </Field>
          );
        })}
      </div>
      <div className="modal__foot" style={{ margin: '0 calc(-1 * var(--space-5)) calc(-1 * var(--space-5))' }}>
        <Button variant="ghost" onClick={onClose} disabled={salvando}>Cancelar</Button>
        <Button variant="primary" onClick={() => void salvar()} loading={salvando}>Salvar config</Button>
      </div>
    </>
  );
}
